import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { trackClick } from '@/services/tracking';
import { trackSelectPromotion } from '@/lib/analytics';
import { Badge, Img, Skeleton } from '@/components/ui';
import { useRouteLocale } from '@/hooks/useRouteLocale';
import { promotionPhase } from '@/seo/lifecycle';
import { displayName } from '@/seo/text';
import { entityPath } from '@/seo/urls';
import type { Media, Promotion } from '@/types';
import { CONTENT_COLUMN_SIZES, ImagePresets, pickPromotionCover } from '@/utils/safeImageUrl';

interface HeroCarouselProps {
  promotions: Promotion[];
  loading?: boolean;
}

const AUTO_ADVANCE_MS = 5000;

/** Hero artwork: THUMBNAIL, then VIDEO_THUMBNAIL, never the video playlist itself. */
const pickHeroMedia = pickPromotionCover;

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** The user's reduced-motion setting; false where there is no `window.matchMedia` (the server). */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(REDUCED_MOTION_QUERY).matches
  );
}

export function HeroCarousel({ promotions, loading = false }: HeroCarouselProps) {
  const locale = useRouteLocale();
  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  // Read in the initializer so auto-advance never starts for a reduced-motion
  // user, but only in a browser: the server has no `window` (web plan W4.2).
  // Nothing rendered depends on it, so the hydrated markup matches the server's.
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);

  const count = promotions.length;
  const active = count > 0 ? index % count : 0;

  // Slide 0's image is the home page's LCP element. Every slide is stacked in
  // the viewport, so the others' images would download at the same moment and
  // compete with it; they are held back until slide 0's image has settled
  // (or their slide comes up first).
  const [imagesPrimed, setImagesPrimed] = useState(false);
  const primeOthers = useCallback(() => setImagesPrimed(true), []);
  const othersPrimed = imagesPrimed || !pickHeroMedia(promotions[0]?.media);

  // Keep the index in range when the promotions list changes.
  useEffect(() => {
    setIndex((i) => (count > 0 ? i % count : 0));
  }, [count]);

  // Track the user's reduced-motion preference.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(REDUCED_MOTION_QUERY);
    const handleChange = () => setReducedMotion(mq.matches);
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  // Auto-advance every 5s; paused while hovered or focused, skipped entirely
  // for reduced-motion users, and idle when the tab is hidden.
  useEffect(() => {
    if (count <= 1 || hovered || focused || reducedMotion) return;
    const id = setInterval(() => {
      if (document.hidden) return;
      setIndex((i) => (i + 1) % count);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(id);
  }, [count, hovered, focused, reducedMotion]);

  const goPrev = useCallback(() => {
    if (count <= 1) return;
    setIndex((i) => (i - 1 + count) % count);
  }, [count]);

  const goNext = useCallback(() => {
    if (count <= 1) return;
    setIndex((i) => (i + 1) % count);
  }, [count]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goPrev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goNext();
    }
  };

  if (loading) {
    return <Skeleton className="h-[320px] w-full rounded-3xl md:h-[420px]" />;
  }

  if (count === 0) {
    return (
      <div className="relative flex h-[320px] flex-col items-center justify-center gap-4 overflow-hidden rounded-3xl bg-gradient-to-br from-primary to-primary-dark p-8 text-center md:h-[420px]">
        <img
          src="/logo-128.jpg"
          alt="PromoStork"
          width="64"
          height="64"
          className="h-16 w-16 rounded-2xl object-contain"
        />
        {/* Not a heading: the page's H1 and section H2s carry the outline. */}
        <p className="text-2xl font-extrabold text-white md:text-4xl">
          Discover deals around you
        </p>
      </div>
    );
  }

  return (
    <section
      role="region"
      aria-roledescription="carousel"
      aria-label="Featured promotions"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        // focus-within semantics: only unpause when focus leaves the section.
        if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false);
      }}
      className="relative h-[320px] overflow-hidden rounded-3xl md:h-[420px]"
    >
      {promotions.map((promotion, i) => {
        const isActive = i === active;
        // The cleaned names (styled Unicode, emoji, hashtags out) — the
        // backend's clean headline first (B3.5) — as on the cards.
        const title = displayName(promotion.headline) || displayName(promotion.name);
        const brandName = displayName(promotion.brand?.name);
        return (
          <Link
            key={promotion.id}
            to={entityPath(promotion, 'promotion')}
            onClick={() => {
              trackClick('promotion', promotion.id);
              trackSelectPromotion({
                promotionId: promotion.id,
                promotionName: promotion.name,
                creativeSlot: `hero_carousel_${i + 1}`,
              });
            }}
            aria-hidden={!isActive}
            tabIndex={isActive ? 0 : -1}
            className={`absolute inset-0 transition-opacity duration-700 ${
              isActive ? 'z-[1] opacity-100' : 'pointer-events-none opacity-0'
            }`}
          >
            <Img
              media={i === 0 || isActive || othersPrimed ? pickHeroMedia(promotion.media) : null}
              transform={ImagePresets.hero}
              sizes={CONTENT_COLUMN_SIZES}
              priority={i === 0}
              onSettled={i === 0 ? primeOthers : undefined}
              alt={title}
              className="h-full w-full object-cover"
              fallback={
                <div className="h-full w-full bg-gradient-to-br from-primary to-primary-dark" />
              }
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/70" />
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10">
              {/* From the dates only (master D-4), never the isUpcoming flag. */}
              {promotionPhase(promotion, new Date(), locale) === 'upcoming' && <Badge color="warning">Upcoming</Badge>}
              {/* A slide title, not a heading (web plan W0.6): six rotating
                  promotion names would otherwise outrank the page's H1 and
                  section H2s in the outline. */}
              <p className="mt-2 max-w-2xl text-2xl font-extrabold text-white line-clamp-2 md:text-4xl">
                {title}
              </p>
              {brandName && (
                <p className="mt-1 text-sm text-white/90 md:text-base">at {brandName}</p>
              )}
              <p className="mt-3 text-sm font-semibold text-white/80">View deal →</p>
            </div>
          </Link>
        );
      })}

      {count > 1 && (
        <>
          <button
            type="button"
            onClick={goPrev}
            aria-label="Previous slide"
            className="absolute left-4 top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-surface/80 p-2.5 text-ink shadow backdrop-blur transition-all duration-200 hover:bg-surface md:flex"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            type="button"
            onClick={goNext}
            aria-label="Next slide"
            className="absolute right-4 top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-surface/80 p-2.5 text-ink shadow backdrop-blur transition-all duration-200 hover:bg-surface md:flex"
          >
            <ChevronRight size={20} />
          </button>

          {/* Each dot is a 24×24 px button (WCAG 2.5.8 target size) around the
              same small dot as before; bottom-[7px] keeps the dots' centre
              where it was (16 px + half the old 6 px row). */}
          <div className="absolute bottom-[7px] left-1/2 z-10 flex -translate-x-1/2 items-center">
            {promotions.map((promotion, i) => (
              <button
                key={promotion.id}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === active}
                className="group/dot flex h-6 w-6 items-center justify-center"
              >
                <span
                  aria-hidden="true"
                  className={`h-1.5 rounded-full transition-all duration-200 ${
                    i === active ? 'w-5 bg-white' : 'w-1.5 bg-white/40 group-hover/dot:bg-white/60'
                  }`}
                />
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

export default HeroCarousel;
